/**
 * cefrVideoLevel — DEGRADED FALLBACK ONLY. Lives next to the generator it feeds
 * (`generateActivitiesForLesson`), not in a UI dir.
 *
 * iOS keys spelling gating off the REAL per-chapter numeric `videoLevel`
 * (`useChapterInfo`) and Super-Beginner off the REAL `course.level.difficulty`
 * — NOT off the user's CEFR placement. The host should thread those real values
 * into `generateActivitiesForLesson` (see `PersonalizedActivities`' `videoLevel`
 * / `courseDifficulty` props). These placement→level mappings are a last-resort
 * approximation used only when the real chapter/course data is unavailable, so
 * a level-less lesson still gets a sensible spelling band instead of none.
 *
 * The bands mirror iOS `getMaxWordLengthForLevel`:
 *   A0 (<1.0) → no spelling, lower-A1 (1.0–1.5) → ≤5, upper-A1/lower-A2
 *   (1.5–2.5) → ≤7, B1+ (2.5+) → ≤11.
 */
import type { CEFRLevel } from "@shared_types";

/** Numeric video level (matches the iOS spelling-gate bands). */
export function cefrToVideoLevel(cefr: CEFRLevel | undefined): number {
  switch (cefr) {
    // No usable placement signal → mirror iOS's missing-`videoLevel` fallback
    // of `?? 1.0` (LessonScreen.tsx:275 — Lower A1, ≤5-letter spelling allowed).
    // Returning 0.5 here would treat "no level" as "no spelling at all", which
    // iOS never does, so no-placement users would never get a Write activity.
    case "No Level":
    case undefined:
      return 1.0; // Lower A1 fallback — ≤5-letter spelling enabled
    // Explicit A0 is a *true* absolute-beginner placement: suppress spelling on
    // purpose (deliberate divergence from the no-signal fallback above).
    case "A0":
    case "A0 - Scratch":
      return 0.5; // A0 — no spelling
    case "Lower A1":
      return 1.2; // ≤5
    case "Upper A1":
    case "Lower A2":
      return 2.0; // ≤7
    case "Upper A2":
    case "Lower B1":
      return 2.6; // B1+ → ≤11
    default:
      return 3.0; // B1+/B2/C — ≤11
  }
}
